import dayjs from 'utils/dayjs';
import type {
    CredentialingStatus,
    StatusMeta,
    StatusBucket,
    PanelStatus,
    UserPlatformStatus,
    TCredentialingPanelRow,
    TCredentialingActionItem,
} from 'types/credentialing';
import { ECredentialingStatus } from 'types/credentialing';

export const STATUS_META: Record<CredentialingStatus, StatusMeta> = {
    packet_review: {
        label: 'In review',
        badge: 'reviewing',
        bucket: 'inprogress',
        icon: 'clock',
        dict: 'Credentialing Packet Review in Progress',
        tip: 'Klarity is reviewing your eligibility and credentialing packet. No action needed from you yet.',
    },
    packet_incomplete: {
        label: 'Action needed',
        badge: 'action',
        bucket: 'action',
        icon: 'alert',
        dict: 'Credentialing Packet Incomplete',
        tip: 'A required item is missing or invalid. Update it so we can continue.',
    },
    validated: {
        label: 'Validated',
        badge: 'validated',
        bucket: 'inprogress',
        icon: 'check',
        dict: 'Credentialing Packet Validated by Klarity',
        tip: 'Your packet passed Klarity review and is ready to send to the payer.',
    },
    submitting_to_payer: {
        label: 'Submitting to payer',
        badge: 'submitting_to_payer',
        bucket: 'inprogress',
        icon: 'send',
        dict: 'Submitting to Payer',
        tip: 'Klarity is submitting your application to the payer. This step can take a few days.',
    },
    submitted_to_payer: {
        label: 'Submitted to payer',
        badge: 'submitted_to_payer',
        bucket: 'inprogress',
        icon: 'send',
        dict: 'Credentialing Packet Submitted To Payer',
        tip: 'Your application has been sent to the payer and is waiting for their review.',
    },
    payer_review: {
        label: 'Payer reviewing',
        badge: 'reviewing',
        bucket: 'inprogress',
        icon: 'clock',
        dict: 'Payer Review in Progress',
        tip: 'The payer is reviewing your application. No action unless they ask for something.',
    },
    payer_rfi: {
        label: 'Action needed',
        badge: 'action',
        bucket: 'action',
        icon: 'alert',
        dict: 'Payer Review in Progress (RFI, provider action)',
        tip: 'The payer asked for more information or an updated document before they can decide. Update the requested items.',
    },
    signature_needed: {
        label: 'Awaiting approval',
        badge: 'action',
        bucket: 'action',
        icon: 'alert',
        dict: 'Payer Contract Signature Needed',
        tip: 'The payer approved you and emailed a contract. Please sign it.',
    },
    approved: {
        label: 'Approved',
        badge: 'approved',
        bucket: 'approved',
        icon: 'check',
        dict: 'Credentialing Approved',
        tip: 'The payer approved you, with effective date and contract confirmed. Billing setup comes next. Approved is not the same as billing being live.',
    },
    billing_setup: {
        label: 'Setting up billing',
        badge: 'reviewing',
        bucket: 'approved',
        icon: 'clock',
        dict: 'Billing Set-up in Progress',
        tip: 'You are approved. We are configuring billing so your claims can route through Klarity. This is separate from being live.',
    },
    billing_active: {
        label: 'Approved',
        badge: 'approved',
        bucket: 'approved',
        icon: 'check',
        dict: 'Billing Active',
        tip: 'Billing is live. Eligible visits can route through Klarity Managed Billing.',
    },
    disapproved: {
        label: 'Rejected',
        badge: 'not_approved',
        bucket: 'notapproved',
        icon: 'x',
        dict: 'Credentialing Disapproved',
        tip: 'The payer declined this application. Contact support to review your options.',
    },
    paused: {
        label: 'Paused',
        badge: 'paused',
        bucket: 'notapproved',
        icon: 'clock',
        dict: 'Paused',
        tip: 'Work on this panel is stopped or on hold. Review the next option with Klarity.',
    },
    withdrawn: {
        label: 'Withdrawn',
        badge: 'paused',
        bucket: 'notapproved',
        icon: 'clock',
        dict: 'Withdrawn (provider-initiated)',
        tip: 'You withdrew this panel. You can resubmit it anytime from Select payers.',
    },
    deposit_failed: {
        label: 'Payment needed',
        badge: 'action',
        bucket: 'action',
        icon: 'alert',
        dict: 'Deposit gate (PAYMENT_REQUIRED / FAILED)',
        tip: 'Your $50 per-panel deposit did not go through, so credentialing has not started. Update your payment to begin.',
    },
    payment_pending: {
        label: 'Awaiting payment',
        badge: 'action',
        bucket: 'action',
        icon: 'alert',
        dict: 'Submitted — Payment Pending',
        tip: 'Your application is submitted. Your $50 per-panel deposit is awaiting payment. Pay it so we can start credentialing.',
    },
    // 兜底档：后端给了前端还不认识的 ACTION_NEEDED 状态时用它。文案必须保持中性——
    // 猜成 payment_pending 会对已付款的 provider 谎报欠费（见 2026-08 SIGNATURE_NEEDED 事故）。
    action_needed: {
        label: 'Action needed',
        badge: 'action',
        bucket: 'action',
        icon: 'alert',
        dict: 'Action Needed',
        tip: 'This panel needs something from you. See the Action needed section above for the next step.',
    },
    panel_closed: {
        label: 'Panel closed',
        badge: 'panel_closed',
        bucket: 'panelclosed',
        icon: 'x',
        dict: 'Panel Closed',
        tip: 'The payer has stopped accepting new applications. This is not a decision about you.',
    },
};

export function getStatusMeta(status: CredentialingStatus): StatusMeta {
    return STATUS_META[status] || STATUS_META.packet_review;
}

/**
 * 后端把同一个 PENDING_PAYMENT 拆成三种 pill，共用 payment_pending 这一个 status code：
 * "Awaiting payment" = 首次稍后付款、还没绑卡；"Payment needed" = 扣款失败；
 * "Payment processing" = 卡已在档、只等自动扣款。
 * 前端唯一能区分的信号就是 statusDisplay 文案，所以徽章配色和 tooltip 跟着它走。
 * STATUS_META 里的 payment_pending 按首次待付（"Awaiting payment"）兜底——
 * 后端没给 statusDisplay 时宁可多提示一次付款，也不要把该做的动作藏起来。
 */
const DISPLAY_OVERRIDES: Record<string, Partial<StatusMeta>> = {
    'Payment needed': {
        tip: STATUS_META.deposit_failed.tip,
    },
    'Payment processing': {
        badge: 'processing',
        tip: 'Your card is on file and we are charging the $50 per-panel deposit. Nothing for you to do — credentialing starts once it clears.',
    },
};

/**
 * SIGNATURE_NEEDED 签前签后 status / statusDisplay 都不变（后端只盖 payer_contract_signed_at），
 * 所以已签的 provider 不能再看到催签文案——tooltip 按 contractSigned 分两句。
 */
/** 押金被 Ops 减免的账号：新 panel 直接进 In review，tooltip 说明为什么没有付款步骤 */
export const FEE_WAIVED_REVIEW_TIP =
    'Your application is submitted. Your credentialing fee is waived by Klarity, so credentialing has started.';

export const SIGNED_CONTRACT_TIP = "You've signed the contract. The payer is finalizing your approval — no action needed from you.";

/** 徽章展示用的 meta：先取 status 的固定映射，再按后端文案覆盖，最后按「合同已签」换 tooltip。 */
export function getBadgeMeta(
    status: CredentialingStatus,
    displayText?: string,
    contractSigned?: boolean,
    feeWaived?: boolean,
): StatusMeta {
    const base = getStatusMeta(status);
    const override = displayText ? DISPLAY_OVERRIDES[displayText] : undefined;
    const meta = override ? { ...base, ...override } : base;
    if (status === 'signature_needed' && contractSigned) return { ...meta, tip: SIGNED_CONTRACT_TIP };
    if (status === 'packet_review' && feeWaived) return { ...meta, tip: FEE_WAIVED_REVIEW_TIP };
    return meta;
}

/**
 * 押金 PAYMENT 行动卡文案。后端同一个 PENDING_PAYMENT 下按 cta 分两种：
 * ADD_PAYMENT = 首次待付（还没绑卡），FIX_PAYMENT = 扣款失败。首次待付不能显示失败文案，
 * 否则医生会以为扣款失败了；多条里只要有一条失败就按失败提示（更紧急）。
 */
export function getPaymentActionCopy(items: TCredentialingActionItem[]) {
    const failed = items.find((it) => it.cta === 'FIX_PAYMENT');
    if (failed) {
        return {
            item: failed,
            name: 'Payment needed',
            detail: failed.reason || 'Your deposit is missing or failed. Update your payment to continue.',
            btnText: 'Update payment',
        };
    }
    return {
        item: items[0],
        name: 'Pay your deposit to start credentialing',
        detail: "Your application is submitted. Pay your $50 per-panel deposit and we'll start sending your applications to payers.",
        btnText: 'Pay deposit',
    };
}

export function getStatusBucket(status: CredentialingStatus): StatusBucket {
    return getStatusMeta(status).bucket;
}

export const PROGRESS_STEPS = ['In review', 'Submitting', 'Submitted', 'Payer review', 'Approved'];

export const STATUS_STEP_MAP: Partial<Record<CredentialingStatus, number>> = {
    payment_pending: 0,
    packet_review: 0,
    packet_incomplete: 0,
    validated: 0,
    deposit_failed: 0,
    submitting_to_payer: 1,
    submitted_to_payer: 2,
    payer_review: 3,
    payer_rfi: 3,
    signature_needed: 3,
    approved: 4,
    billing_setup: 4,
    billing_active: 4,
};

export const BUCKET_ORDER: StatusBucket[] = ['action', 'inprogress', 'approved', 'notapproved', 'panelclosed'];

export const STATUS_FILTER_OPTS: { b: StatusBucket; label: string }[] = [
    { b: 'action', label: 'Action needed' },
    { b: 'inprogress', label: 'In progress' },
    { b: 'approved', label: 'Approved' },
    { b: 'notapproved', label: 'Rejected' },
    { b: 'panelclosed', label: 'Panel closed' },
];

export const ALL_STATUS_BUCKETS: StatusBucket[] = STATUS_FILTER_OPTS.map((o) => o.b);

export const TIER_DAYS: Record<UserPlatformStatus, number> = { flash: 5, priority: 15, standard: 50 };

/* eslint-disable @typescript-eslint/naming-convention */
export const STATUS_DETAIL_MAP: Record<CredentialingStatus, (name: string) => string> = {
    packet_review: (n) => `Received. Our credentialing team is reviewing your eligibility and profile for ${n}.`,
    packet_incomplete: (n) => `We need a missing or corrected item before we can continue your ${n} application.`,
    validated: (n) => `Your ${n} packet passed Klarity review and is ready to send to the payer.`,
    submitting_to_payer: (n) => `Klarity is submitting your application to ${n}. This step can take a few days.`,
    submitted_to_payer: (n) =>
        `Submitted to ${n}. Insurance review usually takes 2 to 3 months. We'll update you here as it moves.`,
    payer_review: (n) => `${n} is reviewing your application.`,
    payer_rfi: (n) => `${n} asked for more information before they can decide. Please respond to keep things moving.`,
    signature_needed: (n) => `${n} approved you and emailed a contract. Please sign it.`,
    approved: (n) => `${n} approved you. We're setting up billing next.`,
    billing_setup: (n) => `You're approved with ${n}. We're configuring claim routing; billing is not live yet.`,
    billing_active: (n) => `Billing is live for ${n}. Eligible visits can route through Klarity Managed Billing.`,
    disapproved: (n) => `${n} did not approve this application. Review the next step with Klarity.`,
    paused: (n) => `Work on ${n} is paused. Review the next option with Klarity.`,
    deposit_failed: (n) =>
        `Your deposit didn't go through, so work on ${n} hasn't started. Update your payment to begin.`,
    payment_pending: (n) => `Waiting on your deposit before we submit your ${n} application.`,
    panel_closed: (n) => `${n} has stopped accepting new applications. This is not a decision about you.`,
    action_needed: (n) => `${n} needs something from you. See the Action needed section above for the next step.`,
};
/* eslint-enable @typescript-eslint/naming-convention */

const API_STATUS_TO_CRED: Record<string, CredentialingStatus> = {
    PENDING_PAYMENT: 'payment_pending',
    KLARITY_REVIEWING: 'packet_review',
    KLARITY_RFI: 'packet_incomplete',
    INFO_NEEDED: 'packet_incomplete',
    VALIDATED: 'validated',
    WAIT_SUBMIT: 'validated',
    SUBMITTING: 'submitting_to_payer',
    SUBMITTED_TO_PAYER: 'submitted_to_payer',
    PAYER_REVIEWING: 'payer_review',
    PAYER_RFI: 'payer_rfi',
    SIGNATURE_NEEDED: 'signature_needed',
    APPROVED: 'approved',
    BILLING_SETUP: 'billing_setup',
    BILLING_ACTIVE: 'billing_active',
    DISAPPROVED: 'disapproved',
    REJECTED: 'disapproved',
    PAUSED: 'paused',
    WITHDRAWN: 'withdrawn',
    DEPOSIT_FAILED: 'deposit_failed',
    PANEL_CLOSED: 'panel_closed',
};

const BUCKET_FALLBACK: Record<string, CredentialingStatus> = {
    ACTION_NEEDED: 'action_needed',
    IN_PROGRESS: 'packet_review',
    APPROVED: 'approved',
    LIVE: 'approved',
    REJECTED: 'disapproved',
    PAUSE: 'paused',
    WITHDRAWN: 'withdrawn',
    PANEL_CLOSED: 'panel_closed',
};

export function mapApiStatusToCred(apiStatus: string, apiBucket: string): CredentialingStatus {
    return API_STATUS_TO_CRED[apiStatus] || BUCKET_FALLBACK[apiBucket] || 'packet_review';
}

export function mapApiBucketToFrontend(apiBucket: string): StatusBucket {
    const map: Record<string, StatusBucket> = {
        ACTION_NEEDED: 'action',
        IN_PROGRESS: 'inprogress',
        APPROVED: 'approved',
        LIVE: 'approved',
        REJECTED: 'notapproved',
        PAUSE: 'notapproved',
        WITHDRAWN: 'notapproved',
        PANEL_CLOSED: 'panelclosed',
    };
    return map[apiBucket] || 'inprogress';
}

export function mapApiPanelRow(row: TCredentialingPanelRow): PanelStatus {
    const status = mapApiStatusToCred(row.status, row.statusBucket);
    const detailFn = STATUS_DETAIL_MAP[status];
    return {
        name: row.payerName,
        klarityPayerId: row.klarityPayerId,
        providerId: row.providerId,
        apiStatus: row.status,
        status,
        statusBucket: mapApiBucketToFrontend(row.statusBucket),
        statusDisplay: row.statusDisplay,
        state: row.payerState,
        detail: detailFn ? detailFn(row.payerName) : '',
        submitted: row.providerSubmittedAt ? dayjs(row.providerSubmittedAt).format('MM/DD/YYYY') : '-',
        updated: row.lastUpdatedAt ? dayjs(row.lastUpdatedAt).format('MM/DD/YYYY') : '-',
        contractSigned: !!row.payerContractSignedAt,
        payerActive: row.payerActive,
        depositAmountCents: row.depositAmountCents,
    };
}

export function deriveCredentialingStatus(panels: TCredentialingPanelRow[]): ECredentialingStatus {
    if (panels.length === 0) return ECredentialingStatus.IN_REVIEW;

    const buckets = panels.map((p) => p.statusBucket);
    const uniqueBuckets = new Set(buckets);

    if (uniqueBuckets.size === 1) {
        const bucket = buckets[0];
        if (bucket === 'ACTION_NEEDED') return ECredentialingStatus.PAYMENT_PENDING;
        if (bucket === 'IN_PROGRESS') return ECredentialingStatus.IN_REVIEW;
        if (bucket === 'APPROVED') return ECredentialingStatus.APPROVED;
        if (bucket === 'LIVE') return ECredentialingStatus.APPROVED;
        if (bucket === 'REJECTED') return ECredentialingStatus.DISAPPROVED;
        if (bucket === 'PAUSE') return ECredentialingStatus.PAUSED;
        if (bucket === 'WITHDRAWN') return ECredentialingStatus.PAUSED;
        if (bucket === 'PANEL_CLOSED') return ECredentialingStatus.PANEL_CLOSED;
    }

    return ECredentialingStatus.MIXED;
}
